import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Breadcrumbs,
  Link,
  Fade,
  Chip,
  Grid,
  Avatar
} from '@mui/material';
import { ArrowBack, NavigateNext } from '@mui/icons-material';
import { positionAnalysisService, TeamAnalysis, StrategicReasoning, StrategicMatch } from '@/services/positionAnalysisService';
import { leagueService } from '@/services/leagueService';
import { teamService } from '@/services/teamService';
import { standingsService, StandingEntry } from '@/services/standingsService';
import { TeamLogo } from '@/components/TeamLogo';
import FormChip from '@/components/FormChip';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AnalysisLoadingSkeleton } from '@/components/AnalysisLoadingSkeleton';
import { RetryableError } from '@/components/RetryableError';
import { useDelayedSkeleton } from '@/hooks/useDelayedSkeleton';
interface League {
  id: string;
  name: string;
  division?: string;
  logoPath?: string;
  logoUrl?: string;
}

interface Team {
  id: string;
  name: string;
  logoPath?: string;
  logoUrl?: string;
}

export default function TeamAnalysisPage() {
  const { teamId, leagueId } = useParams<{ teamId: string; leagueId: string }>();
  const navigate = useNavigate();

  const [analysis, setAnalysis] = useState<TeamAnalysis | null>(null);
  const [league, setLeague] = useState<League | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const [teamStats, setTeamStats] = useState<StandingEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { showSkeleton, showContent, skeletonWasShown } = useDelayedSkeleton({ loading });

  const fetchData = async () => {
    if (!teamId || !leagueId) {
      setError('Invalid team or league ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch team analysis, league data, team data, and standings in parallel
      const [analysisData, leagueData, teamData, standingsData] = await Promise.all([
        positionAnalysisService.getTeamAnalysis(teamId, leagueId),
        leagueService.getLeague(leagueId),
        teamService.getTeam(teamId),
        standingsService.getStandings(leagueId)
      ]);

      setAnalysis(analysisData);
      setLeague(leagueData);
      setTeam(teamData);
      
      // Find the team's stats from standings
      const teamStanding = standingsData.standings.find(s => s.teamId === teamId);
      setTeamStats(teamStanding || null);
    } catch (err) {
      console.error('Error fetching team analysis:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load team analysis. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [teamId, leagueId]);

  const handleRetry = () => {
    fetchData();
  };

  const handleBackToLeague = () => {
    navigate(`/leagues/${leagueId}/analysis`);
  };

  const generateRequiredFormText = () => {
    // Use the backend's required form description directly
    return analysis?.scenarios?.best?.requiredForm?.description || 'Win all remaining matches';
  };

  const generateAlgorithmicInsight = (reasoning: StrategicReasoning | undefined, targetPoints: number) => {
    if (!reasoning) return '';
    
    const { requiredWins, targetPosition } = reasoning;
    const getOrdinal = (n: number) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    // Get current points from team stats
    const currentPoints = teamStats?.points || 0;

    if (requiredWins === 0) {
      return `Your current ${currentPoints} points already secures ${getOrdinal(targetPosition)} place regardless of remaining results.`;
    }

    const winsText = requiredWins === 1 ? '1 more match' : `${requiredWins} more matches`;
    
    return `Winning ${winsText} brings you from ${currentPoints} to ${targetPoints} points, securing ${getOrdinal(targetPosition)} place.`;
  };

  if (showSkeleton) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Fade in={true} timeout={300}>
          <Box>
            <AnalysisLoadingSkeleton variant="team" />
          </Box>
        </Fade>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <RetryableError
          error={error}
          onRetry={handleRetry}
          title="Failed to load team analysis"
          showDetails
        />
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBackToLeague}
        >
          Back to League Analysis
        </Button>
      </Container>
    );
  }

  if (!showContent) {
    return null;
  }

  if (!analysis || !league || !team) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <RetryableError
          error="No analysis data available for this team."
          onRetry={handleRetry}
          title="Analysis unavailable"
        />
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBackToLeague}
        >
          Back to League Analysis
        </Button>
      </Container>
    );
  }

  const ContentWrapper = ({ children }: { children: React.ReactNode }) => {
    if (skeletonWasShown) {
      return (
        <Fade in={showContent} timeout={300}>
          <Container maxWidth="lg" sx={{ py: 3 }}>
            {children}
          </Container>
        </Fade>
      );
    }
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {children}
      </Container>
    );
  };

  return (
    <ErrorBoundary>
      <ContentWrapper>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNext fontSize="small" />} 
          sx={{ mb: 2 }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/leagues')}
            sx={{ textDecoration: 'none' }}
          >
            Leagues
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToLeague}
            sx={{ textDecoration: 'none' }}
          >
            {league.division ? `${league.name} - ${league.division}` : league.name}
          </Link>
          <Typography variant="body2" color="text.primary">
            {team.name} Analysis
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Box sx={{ mr: 2 }}>
              <TeamLogo 
                teamName={team.name}
                logoPath={team.logoPath}
                logoUrl={team.logoUrl}
                size={48}
              />
            </Box>
            <Box>
              <Typography variant="h4" component="h1">
                {team.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  Current Position:
                </Typography>
                <Box 
                  sx={{ 
                    backgroundColor: 'rgba(33, 150, 243, 0.08)', 
                    color: '#1976d2', 
                    fontWeight: 'bold',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.875rem'
                  }}
                >
                  {analysis.currentPosition === 1 ? '1st' : 
                   analysis.currentPosition === 2 ? '2nd' : 
                   analysis.currentPosition === 3 ? '3rd' : 
                   `${analysis.currentPosition}th`}
                </Box>
                <Typography variant="subtitle1" color="text.secondary">
                  | {teamStats?.points || 0} points
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Current Statistics */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Statistics
            </Typography>
            
            {teamStats ? (
              <>
                {/* Main Statistics Grid */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={2}>
                    <Card elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                      <Typography variant="h4" color="text.secondary">
                        {teamStats.played}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Played
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Card elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
                      <Typography variant="h4" color="success.main">
                        {teamStats.won}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Wins
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Card elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(255, 152, 0, 0.08)' }}>
                      <Typography variant="h4" color="warning.main">
                        {teamStats.drawn}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Draws
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Card elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(244, 67, 54, 0.08)' }}>
                      <Typography variant="h4" color="error.main">
                        {teamStats.lost}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Losses
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Card elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(33, 150, 243, 0.08)' }}>
                      <Typography variant="h4" color="info.main">
                        {teamStats.goalsFor}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Goals For
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Card elevation={0} sx={{
                      p: 2,
                      textAlign: 'center',
                      backgroundColor: teamStats.goalDifference >= 0 ? 'rgba(76, 175, 80, 0.08)' : 'rgba(244, 67, 54, 0.08)'
                    }}>
                      <Typography variant="h4" color={teamStats.goalDifference >= 0 ? 'success.main' : 'error.main'}>
                        {teamStats.goalDifference >= 0 ? '+' : ''}{teamStats.goalDifference}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Goal Diff
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Card elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(33, 150, 243, 0.08)' }}>
                      <Typography variant="h4" color="info.main">
                        {teamStats.points}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Points
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Card elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                      <Typography variant="h4" color="text.secondary">
                        {teamStats.played > 0 ? (teamStats.pointsPerGame !== undefined && teamStats.pointsPerGame !== null ? teamStats.pointsPerGame.toFixed(2) : (teamStats.points / teamStats.played).toFixed(2)) : '0.00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        PPG
                      </Typography>
                    </Card>
                  </Grid>
                  {teamStats.qualityOfPlay !== undefined && (
                    <>
                      <Grid item xs={6} sm={2}>
                        <Card elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(156, 39, 176, 0.08)' }}>
                          <Typography variant="h4" sx={{ color: '#9c27b0' }}>
                            {teamStats.attScore ?? 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Att Score
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <Card elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(0, 150, 136, 0.08)' }}>
                          <Typography variant="h4" sx={{ color: '#009688' }}>
                            {teamStats.defScore ?? 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Def Score
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <Card elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'rgba(255, 87, 34, 0.08)' }}>
                          <Typography variant="h4" sx={{ color: '#ff5722' }}>
                            {teamStats.qualityOfPlay}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            QoP
                          </Typography>
                        </Card>
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Recent Form */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Recent Form (Last 5 Matches)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <FormChip form={teamStats.form.slice(-5)} />
                  </Box>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h3" color="primary">
                  {analysis.currentPosition}
                </Typography>
                <Box>
                  <Typography variant="body1">
                    Current Position
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {analysis.currentPosition === 1 ? '1st' : 
                     analysis.currentPosition === 2 ? '2nd' : 
                     analysis.currentPosition === 3 ? '3rd' : 
                     `${analysis.currentPosition}th`} - {analysis.teamName}
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Position Scenarios */}
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Position Scenarios
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ 
              height: '100%',
              border: '2px solid rgba(76, 175, 80, 0.5)'
            }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(76, 175, 80, 0.08)',
                  borderBottom: '1px solid rgba(76, 175, 80, 0.3)',
                  p: 3,
                  mb: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                      🏆 Best Case
                    </Typography>
                    {!analysis.dependencies.controlsDestiny && (
                      <Chip 
                        label="Depends on others"
                        size="small"
                        sx={{ 
                          backgroundColor: '#ff9800',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem'
                        }}
                      />
                    )}
                  </Box>
                  
                  <Typography variant="h2" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 1 }}>
                    {analysis.scenarios.best.position}
                    <Typography component="span" variant="h5" sx={{ verticalAlign: 'super', fontSize: '0.6em' }}>
                      {analysis.scenarios.best.position === 1 ? 'st' : 
                       analysis.scenarios.best.position === 2 ? 'nd' : 
                       analysis.scenarios.best.position === 3 ? 'rd' : 'th'}
                    </Typography>
                  </Typography>
                  
                  {analysis.scenarios.best.contextualLabel && (
                    <Chip 
                      label={analysis.scenarios.best.contextualLabel}
                      sx={{ 
                        backgroundColor: '#4caf50', 
                        color: 'white', 
                        fontWeight: 'bold'
                      }}
                    />
                  )}
                </Box>
                
                <Box sx={{ px: 3, pb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Required Form
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                    {generateRequiredFormText()}
                  </Typography>

                  {/* Strategic Insights */}
                  {analysis.scenarios.best.strategicReasoning && (
                    <Box sx={{ 
                      mb: 3, 
                      p: 1.5, 
                      backgroundColor: 'rgba(76, 175, 80, 0.08)', 
                      borderRadius: 1,
                      border: '1px solid rgba(76, 175, 80, 0.2)'
                    }}>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                        💡 {generateAlgorithmicInsight(analysis.scenarios.best.strategicReasoning, analysis.scenarios.best.points)}
                      </Typography>
                    </Box>
                  )}

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Upcoming Matches
                  </Typography>
                  
                  {analysis.scenarios.best.strategicReasoning?.upcomingMatches.map((match: StrategicMatch, i: number) => (
                    <Box key={i} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: 'rgba(0,0,0,0.02)',
                      gap: 2
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                        <Avatar 
                          src={match.opponentLogoPath ? `/${match.opponentLogoPath}` : undefined}
                          sx={{ width: 20, height: 20, mr: 1, fontSize: '0.6rem' }}
                        >
                          {match.opponent.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {match.opponent}
                        </Typography>
                      </Box>
                      
                      <Chip 
                        label={match.difficulty}
                        size="small"
                        color={
                          match.difficulty === 'FAVORABLE' ? 'success' :
                          match.difficulty === 'COMPETITIVE' ? 'info' : 'warning'
                        }
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    </Box>
                  ))}

                  {/* External Dependencies */}
                  {analysis.scenarios.best.strategicReasoning?.externalDependencies && 
                   analysis.scenarios.best.strategicReasoning.externalDependencies.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Required Results from Other Matches
                      </Typography>
                      
                      {analysis.scenarios.best.strategicReasoning.externalDependencies.map((dep: string, i: number) => (
                        <Typography key={i} variant="body2" sx={{ 
                          fontSize: '0.75rem', 
                          mb: 0.5,
                          pl: 1,
                          color: 'text.secondary'
                        }}>
                          • {dep}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ 
              height: '100%',
              border: '2px solid rgba(33, 150, 243, 0.5)'
            }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(33, 150, 243, 0.08)',
                  borderBottom: '1px solid rgba(33, 150, 243, 0.3)',
                  p: 3,
                  mb: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                      ✨ Prediction
                    </Typography>
                  </Box>
                  
                  <Typography variant="h2" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 1 }}>
                    {analysis.scenarios.mostLikely.position}
                    <Typography component="span" variant="h5" sx={{ verticalAlign: 'super', fontSize: '0.6em' }}>
                      {analysis.scenarios.mostLikely.position === 1 ? 'st' : 
                       analysis.scenarios.mostLikely.position === 2 ? 'nd' : 
                       analysis.scenarios.mostLikely.position === 3 ? 'rd' : 'th'}
                    </Typography>
                  </Typography>
                  
                  {analysis.scenarios.mostLikely.contextualLabel && (
                    <Chip 
                      label={analysis.scenarios.mostLikely.contextualLabel}
                      sx={{ 
                        backgroundColor: '#2196f3', 
                        color: 'white', 
                        fontWeight: 'bold'
                      }}
                    />
                  )}
                </Box>
                
                <Box sx={{ px: 3, pb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Projected Form
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                    {analysis.scenarios.mostLikely.requiredForm.description}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Projected Results
                  </Typography>
                  {analysis.scenarios.mostLikely.dependencies && analysis.scenarios.mostLikely.dependencies.length > 0 ? (
                    analysis.scenarios.mostLikely.dependencies.slice(0, 4).map((outcome, index) => (
                      <Typography key={index} variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                        {outcome}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                      +2 more matches
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Critical Matches */}
        {analysis.criticalMatches && analysis.criticalMatches.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Critical Upcoming Matches
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These matches have the biggest impact on final position
              </Typography>
              {analysis.criticalMatches.slice(0, 5).map((match) => (
                <Box key={match.matchId} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    vs {match.opponent}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {new Date(match.dateTime).toLocaleDateString()} at {match.location}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, fontSize: '0.875rem' }}>
                    <span>Win: Position {match.impact.win.position}</span>
                    <span>Draw: Position {match.impact.draw.position}</span>
                    <span>Loss: Position {match.impact.loss.position}</span>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
      </ContentWrapper>
    </ErrorBoundary>
  );
}
